import * as React from 'react';
import { useId } from '@mantine/hooks';
import { PatternFormat } from 'react-number-format';
import { Input, createStyles, InputProps } from '@mantine/core';

// Components
import { FieldLabelTooltip } from './FieldLabelTooltip';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';

// Types
import { GenericFieldProps } from '../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        leftGroupPosition: commonFieldStyles.leftGroupPosition,
        rightGroupPosition: commonFieldStyles.rightGroupPosition,
        middleGroupPosition: commonFieldStyles.middleGroupPosition,
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        label: commonFieldStyles.label,
        icon: commonFieldStyles.icon,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description,
        flexGrow: {
            flex: 1
        }
    };
});

export interface CustomPatternNumberInputProps extends InputProps, GenericFieldProps {
    key?: string;
    label?: string;
    readOnly?: boolean;
    allowEmptyFormatting?: boolean;
    pattern: string;
    mask?: string;
    value?: string;
    flexGrow?: boolean;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const PatternNumberInput: React.FC<CustomPatternNumberInputProps> = ({
    readOnly,
    labelKey,
    label,
    icon,
    pattern,
    required,
    mask = '_',
    error,
    descriptionKey,
    tooltipKey,
    groupPosition,
    onChange,
    value,
    flexGrow,
    parentform,
    targetperiod,
    ...props
}) => {
    const { classes, cx } = useStyles();
    const inputLabel = useFieldTranslation({
        key: labelKey,
        label,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const descriptionLabel = useFieldTranslation({
        key: descriptionKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const id = useId();

    return (
        <Input.Wrapper
            id={id}
            label={
                <>
                    <span>{inputLabel}</span>
                    {tooltipKey ? <FieldLabelTooltip translationKey={tooltipKey} /> : null}
                </>
            }
            description={descriptionLabel}
            required={required}
            classNames={{
                root: cx(classes.root, {
                    [classes.flexGrow]: flexGrow
                }),
                label: classes.label,
                required: cx(classes.required, {
                    [classes.hiddenDisplay]: readOnly
                }),
                error: classes.error,
                description: cx(classes.description, {
                    [classes.hiddenDisplay]: readOnly
                })
            }}
            error={error}
        >
            <Input<any>
                icon={icon}
                component={PatternFormat}
                required={required}
                classNames={{
                    input: cx(classes.input, {
                        [classes.readonlyInput]: readOnly,
                        [classes.leftGroupPosition]: groupPosition && groupPosition === 'l',
                        [classes.rightGroupPosition]: groupPosition && groupPosition === 'r',
                        [classes.middleGroupPosition]: groupPosition && groupPosition === 'm'
                    }),
                    icon: cx(classes.icon, {
                        [classes.hiddenDisplay]: readOnly
                    }),
                    wrapper: cx(classes.wrapper, {
                        [classes.readonlyWrapper]: readOnly
                    })
                }}
                format={pattern}
                mask={mask}
                id={id}
                disabled={readOnly}
                error={error}
                onChange={onChange}
                value={value}
                {...props}
            />
        </Input.Wrapper>
    );
};
